import { createBrowserRouter, redirect } from "react-router-dom";
import { queryClient } from "../shared/providers/QueryProvider";
import { getMe } from "../features/auth";
import { qk } from "../lib";
import { Login } from "../features/auth";
import { Signup } from "../features/auth";
import { ResetPassword } from "../features/auth";
import { VerifyInvite } from "../features/auth";
import { RegisterInterest } from "../features/preRegistration";
import {
  Assessment,
  ConnectDevice,
  Completion,
  WhoopCallback,
  FitbitCallback,
} from "../features/onboarding";
import { Dashboard } from "../features/dashboard";
import {
  ProgrammeProfile,
  ManagementDashboard,
  WellnessOverview,
  Recovery,
  SleepAnalytics,
  StrainAndActivity,
  OverallStressAndMind,
  BiometricsAndVitals,
  UserProgress,
  TeamProgress,
  JeopardyInsights,
  MeasureComparisonInsights,
  PredictiveInsights,
  Participants,
  ParticipantDetail,
  ParticipantSettings,
  PreRegistrations,
  Settings,
  ClientsList,
  ClientDetail,
  PlatformSettings,
  NewsletterSubscriptions,
  WellbeingContent,
} from "../features/admin";
import { Faq } from "../features/faq";
import { LoginLayout, AppLayout, AdminLayout, SettingsLayout } from "../shared/layouts";
import { RouteErrorBoundary } from "../shared/components";
import { ROUTES } from "../shared/utils";

export const router = createBrowserRouter([
  {
    element: <LoginLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: ROUTES.LOGIN,
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (session.authenticated) {
            if (session.needs_new_password) return null;
            if (session.needs_consent) return null;
            if (session.consent_declined) return null;
            if (session.needs_onboarding) throw redirect(ROUTES.ASSESSMENT);
            if (session.is_admin) throw redirect(ROUTES.ADMIN_DASHBOARD);
            throw redirect(ROUTES.DASHBOARD);
          }

          return null;
        },
        element: <Login />,
      },
      {
        path: ROUTES.REGISTER_INTEREST,
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (session.authenticated) {
            if (session.needs_new_password) return null;
            if (session.needs_consent) return null;
            if (session.consent_declined) return null;
            if (session.needs_onboarding) throw redirect(ROUTES.ASSESSMENT);
            if (session.is_admin) throw redirect(ROUTES.ADMIN_DASHBOARD);
            throw redirect(ROUTES.DASHBOARD);
          }

          return null;
        },
        element: <RegisterInterest />,
      },
      {
        path: ROUTES.RESET_PASSWORD,
        loader: async ({ request }) => {
          const url = new URL(request.url);
          const token = url.searchParams.get("token");
          const search = token ? `?token=${encodeURIComponent(token)}` : "";
          throw redirect(`${ROUTES.RESET}${search}`);
        },
        element: null,
      },
      {
        path: ROUTES.RESET,
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (session.authenticated) {
            if (session.needs_new_password) return null;
            if (session.needs_consent) return null;
            if (session.consent_declined) return null;
            if (session.needs_onboarding) throw redirect(ROUTES.ASSESSMENT);
            if (session.is_admin) throw redirect(ROUTES.ADMIN_DASHBOARD);
            throw redirect(ROUTES.DASHBOARD);
          }

          return null;
        },
        element: <ResetPassword />,
      },
      {
        path: ROUTES.INVITE,
        loader: async ({ request }) => {
          const url = new URL(request.url);
          const token = url.searchParams.get("token");
          const search = token ? `?token=${encodeURIComponent(token)}` : "";
          throw redirect(`${ROUTES.VERIFY_INVITE}${search}`);
        },
        element: null,
      },
      {
        path: ROUTES.VERIFY_INVITE,
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (session.authenticated) {
            if (session.needs_new_password) return null;
            if (session.needs_consent) return null;
            if (session.consent_declined) return null;
            if (session.needs_onboarding) throw redirect(ROUTES.ASSESSMENT);
            if (session.is_admin) throw redirect(ROUTES.ADMIN_DASHBOARD);
            throw redirect(ROUTES.DASHBOARD);
          }

          return null;
        },
        element: <VerifyInvite />,
      },
      {
        path: ROUTES.SIGN_UP,
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (session.authenticated) {
            if (session.needs_new_password) return null;
            if (session.needs_consent) throw redirect(ROUTES.LOGIN);
            if (session.consent_declined) throw redirect(ROUTES.LOGIN);
            if (session.needs_onboarding) throw redirect(ROUTES.ASSESSMENT);
            if (session.is_admin) throw redirect(ROUTES.ADMIN_DASHBOARD);
            throw redirect(ROUTES.DASHBOARD);
          }

          return null;
        },
        element: <Signup />,
      },
    ],
  },
  {
    element: <AppLayout showAppBar={true} />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: ROUTES.FAQ,
        element: <Faq />,
      },
      {
        path: ROUTES.DASHBOARD,
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);
          if (session.needs_onboarding) throw redirect(ROUTES.ASSESSMENT);
          if (session.is_admin) throw redirect(ROUTES.ADMIN_DASHBOARD);

          return null;
        },
        element: <Dashboard />,
      },
      {
        path: ROUTES.ASSESSMENT,
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);

          if (!session.needs_onboarding) {
            throw redirect(ROUTES.DASHBOARD);
          }

          return null;
        },
        element: <Assessment />,
      },
      {
        path: ROUTES.CONNECT_DEVICE,
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);

          if (session.needs_onboarding) {
            throw redirect(ROUTES.DASHBOARD);
          }

          return null;
        },
        element: <ConnectDevice />,
      },
      {
        path: ROUTES.WHOOP_CALLBACK,
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);

          if (session.needs_onboarding) {
            throw redirect(ROUTES.DASHBOARD);
          }

          return null;
        },
        element: <WhoopCallback />,
      },
      {
        path: ROUTES.FITBIT_CALLBACK,
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);

          if (session.needs_onboarding) {
            throw redirect(ROUTES.DASHBOARD);
          }

          return null;
        },
        element: <FitbitCallback />,
      },
      {
        path: ROUTES.COMPLETE,
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);

          return null;
        },
        element: <Completion />,
      },
      {
        path: "*",
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.is_admin) throw redirect(ROUTES.ADMIN_DASHBOARD);
          throw redirect(ROUTES.DASHBOARD);
        },
        element: null,
      },
    ],
  },
  {
    element: <AdminLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: ROUTES.ADMIN_DASHBOARD,
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);
          if (!session.is_admin) throw redirect(ROUTES.DASHBOARD);

          return null;
        },
        element: <ProgrammeProfile />,
      },
      {
        path: "/admin/dashboard",
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);
          if (!session.is_admin) throw redirect(ROUTES.DASHBOARD);

          return null;
        },
        element: <ManagementDashboard />,
      },
      {
        path: "/admin/wellness-overview",
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);
          if (!session.is_admin) throw redirect(ROUTES.DASHBOARD);

          return null;
        },
        element: <WellnessOverview />,
      },
      {
        path: "/admin/recovery",
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);
          if (!session.is_admin) throw redirect(ROUTES.DASHBOARD);

          return null;
        },
        element: <Recovery />,
      },
      {
        path: "/admin/sleep-analytics",
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);
          if (!session.is_admin) throw redirect(ROUTES.DASHBOARD);

          return null;
        },
        element: <SleepAnalytics />,
      },
      {
        path: "/admin/strain-activity",
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);
          if (!session.is_admin) throw redirect(ROUTES.DASHBOARD);

          return null;
        },
        element: <StrainAndActivity />,
      },
      {
        path: "/admin/stress-mind",
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);
          if (!session.is_admin) throw redirect(ROUTES.DASHBOARD);

          return null;
        },
        element: <OverallStressAndMind />,
      },
      {
        path: "/admin/biometrics",
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);
          if (!session.is_admin) throw redirect(ROUTES.DASHBOARD);

          return null;
        },
        element: <BiometricsAndVitals />,
      },
      {
        path: "/admin/user-progress",
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);
          if (!session.is_admin) throw redirect(ROUTES.DASHBOARD);

          return null;
        },
        element: <UserProgress />,
      },
      {
        path: "/admin/team-progress",
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);
          if (!session.is_admin) throw redirect(ROUTES.DASHBOARD);

          return null;
        },
        element: <TeamProgress />,
      },
      {
        path: ROUTES.JEOPARDY_INSIGHTS,
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);
          if (!session.is_admin) throw redirect(ROUTES.DASHBOARD);

          return null;
        },
        element: <JeopardyInsights />,
      },
      {
        path: ROUTES.MEASURE_COMPARISON_INSIGHTS,
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);
          if (!session.is_admin) throw redirect(ROUTES.DASHBOARD);

          return null;
        },
        element: <MeasureComparisonInsights />,
      },
      {
        path: ROUTES.PREDICTIVE_INSIGHTS,
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);
          if (!session.is_admin) throw redirect(ROUTES.DASHBOARD);

          return null;
        },
        element: <PredictiveInsights />,
      },
      // ── Redirects for old standalone routes ──────────────────────────────
      {
        path: ROUTES.WELLBEING_CONTENT,
        loader: async () => { throw redirect(ROUTES.SETTINGS_WELLBEING_CONTENT); },
        element: null,
      },
      {
        path: ROUTES.PRE_REGISTRATIONS,
        loader: async () => { throw redirect(ROUTES.SETTINGS_PRE_REGISTRATIONS); },
        element: null,
      },
      {
        path: ROUTES.PARTICIPANTS,
        loader: async () => { throw redirect(ROUTES.SETTINGS_PARTICIPANTS); },
        element: null,
      },
      {
        path: `${ROUTES.PARTICIPANTS}/:participantId`,
        loader: async ({ params }) => {
          throw redirect(`${ROUTES.SETTINGS_PARTICIPANTS}/${params.participantId}`);
        },
        element: null,
      },
      {
        path: ROUTES.PARTICIPANT_SETTINGS,
        loader: async () => { throw redirect(ROUTES.SETTINGS_PARTICIPANT_SETTINGS); },
        element: null,
      },
      // ── Settings hub (nested layout + sub-routes) ─────────────────────────
      {
        path: ROUTES.SETTINGS,
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });
          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);
          if (!session.is_admin) throw redirect(ROUTES.DASHBOARD);
          // Redirect /admin/settings to the clients list (or to own client for non-central)
          if (session.user?.is_central_admin) {
            throw redirect(ROUTES.SETTINGS_CLIENTS);
          }
          const clientId = session.user?.client_id;
          if (clientId) {
            throw redirect(`${ROUTES.SETTINGS_CLIENTS}/${clientId}`);
          }
          throw redirect(ROUTES.SETTINGS_CLIENTS);
        },
        element: null,
      },
      {
        path: `${ROUTES.SETTINGS}/*`,
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });
          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);
          if (!session.is_admin) throw redirect(ROUTES.DASHBOARD);
          return null;
        },
        element: <SettingsLayout />,
        children: [
          { path: "clients", element: <ClientsList /> },
          { path: "clients/:clientId", element: <ClientDetail /> },
          { path: "platform", element: <PlatformSettings /> },
          { path: "participants", element: <Participants /> },
          { path: "participants/:participantId", element: <ParticipantDetail /> },
          { path: "participant-settings", element: <ParticipantSettings /> },
          { path: "pre-registrations", element: <PreRegistrations /> },
          { path: "wellbeing-content", element: <WellbeingContent /> },
          { path: "newsletter-subscriptions", element: <NewsletterSubscriptions /> },
        ],
      },
      {
        path: "/admin/configurations",
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (session.needs_new_password) throw redirect(ROUTES.LOGIN);
          if (session.needs_consent) throw redirect(ROUTES.LOGIN);
          if (session.consent_declined) throw redirect(ROUTES.LOGIN);
          if (!session.is_admin) throw redirect(ROUTES.DASHBOARD);

          throw redirect(ROUTES.SETTINGS);
        },
        element: null,
      },
      {
        path: "/admin/*",
        loader: async () => {
          const session = await queryClient.ensureQueryData({
            queryKey: qk.me(),
            queryFn: getMe,
          });

          if (!session.authenticated) throw redirect(ROUTES.LOGIN);
          if (!session.is_admin) throw redirect(ROUTES.DASHBOARD);
          throw redirect(ROUTES.ADMIN_DASHBOARD);
        },
        element: null,
      },
    ],
  },
]);
